export type BillingCycle = 'monthly' | 'yearly';

const DEFAULT_CURRENCY = 'USD';

const pickFirstString = (...values: unknown[]): string | undefined => {
  for (const value of values) {
    if (typeof value !== 'string') continue;
    const trimmed = value.trim();
    if (trimmed) return trimmed;
  }
  return undefined;
};

const toNumber = (value: unknown): number | undefined => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string') {
    const cleaned = value.replace(/[,_\s]/g, '').trim();
    if (!cleaned) return undefined;
    const parsed = Number(cleaned);
    return Number.isFinite(parsed) ? parsed : undefined;
  }
  return undefined;
};

const pickFirstNumber = (...values: unknown[]): number | undefined => {
  for (const value of values) {
    const parsed = toNumber(value);
    if (typeof parsed === 'number') return parsed;
  }
  return undefined;
};

const normalizeCycleKey = (value: string): string =>
  value
    .trim()
    .toLowerCase()
    .replace(/[^a-z]/g, '');

const getCycleAliases = (cycle: BillingCycle): string[] =>
  cycle === 'yearly'
    ? ['yearly', 'year', 'annual', 'annually']
    : ['monthly', 'month'];

const getSubscriptionMetadata = (plan: any) => plan?.metadata?.subscription || {};

const normalizeCurrency = (value?: string): string => {
  const normalized = String(value || '').trim().toUpperCase();
  return /^[A-Z]{3}$/.test(normalized) ? normalized : DEFAULT_CURRENCY;
};

export const getPlanCurrency = (plan: any): string => {
  const subscriptionMeta = getSubscriptionMetadata(plan);
  return normalizeCurrency(
    pickFirstString(
      subscriptionMeta?.currency,
      subscriptionMeta?.currencyCode,
      plan?.currency,
      DEFAULT_CURRENCY,
    ),
  );
};

export const formatPriceAmount = (
  amount?: number,
  currency = DEFAULT_CURRENCY,
): string => {
  if (typeof amount !== 'number' || Number.isNaN(amount)) return 'Custom pricing';

  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: normalizeCurrency(currency),
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
};

export const normalizeBillingCycle = (value?: unknown): BillingCycle => {
  const normalized = normalizeCycleKey(String(value || ''));
  if (['yearly', 'year', 'annual', 'annually'].includes(normalized))
    return 'yearly';
  return 'monthly';
};

const getPriceFromMap = (
  map: Record<string, unknown> | undefined,
  cycle: BillingCycle,
): number | undefined => {
  if (!map || typeof map !== 'object') return undefined;
  const aliases = getCycleAliases(cycle);

  for (const [rawKey, rawValue] of Object.entries(map)) {
    if (!aliases.includes(normalizeCycleKey(rawKey))) continue;
    const parsed = toNumber(rawValue);
    if (typeof parsed === 'number') return parsed;
  }
  return undefined;
};

export const getPlanCyclePrice = (
  plan: any,
  cycle: BillingCycle,
): number | undefined => {
  const subscriptionMeta = getSubscriptionMetadata(plan);
  const pricingMap =
    subscriptionMeta?.amounts ||
    subscriptionMeta?.pricing ||
    subscriptionMeta?.priceAmounts;

  const amountFromMap = getPriceFromMap(pricingMap, cycle);
  if (typeof amountFromMap === 'number') return amountFromMap;

  if (cycle === 'yearly') {
    return pickFirstNumber(
      subscriptionMeta?.yearlyPrice,
      subscriptionMeta?.annualPrice,
      subscriptionMeta?.priceYearly,
      subscriptionMeta?.priceAnnual,
    );
  }

  return pickFirstNumber(
    subscriptionMeta?.monthlyPrice,
    subscriptionMeta?.priceMonthly,
    plan?.price,
  );
};

const getPriceIdFromMap = (
  map: Record<string, unknown> | undefined,
  cycle: BillingCycle,
): string | undefined => {
  if (!map || typeof map !== 'object') return undefined;
  const aliases = getCycleAliases(cycle);

  for (const [rawKey, rawValue] of Object.entries(map)) {
    if (!aliases.includes(normalizeCycleKey(rawKey))) continue;
    const value = pickFirstString(rawValue);
    if (value) return value;
  }
  return undefined;
};

export const getPlanCyclePriceID = (
  plan: any,
  cycle: BillingCycle,
): string | undefined => {
  const subscriptionMeta = getSubscriptionMetadata(plan);
  const priceFromMap = getPriceIdFromMap(subscriptionMeta?.prices, cycle);
  if (priceFromMap) return priceFromMap;

  if (cycle === 'yearly') {
    return pickFirstString(
      subscriptionMeta?.yearlyPriceID,
      subscriptionMeta?.yearlyPriceId,
      subscriptionMeta?.annualPriceID,
      subscriptionMeta?.annualPriceId,
    );
  }

  return pickFirstString(
    subscriptionMeta?.monthlyPriceID,
    subscriptionMeta?.monthlyPriceId,
    subscriptionMeta?.priceID,
    subscriptionMeta?.priceId,
  );
};

export const getDefaultPlanBillingCycle = (plan: any): BillingCycle => {
  const subscriptionMeta = getSubscriptionMetadata(plan);
  return normalizeBillingCycle(
    subscriptionMeta?.defaultBillingCycle || subscriptionMeta?.billingCycle,
  );
};

export const addCycleToDate = (
  baseDate: Date,
  cycle: BillingCycle,
): Date => {
  const nextDate = new Date(baseDate);
  if (cycle === 'yearly') {
    nextDate.setFullYear(nextDate.getFullYear() + 1);
    return nextDate;
  }
  nextDate.setMonth(nextDate.getMonth() + 1);
  return nextDate;
};

export const formatCycleLabel = (cycle: BillingCycle): string =>
  cycle === 'yearly' ? 'Yearly' : 'Monthly';

export const formatPlanCyclePrice = (
  plan: any,
  cycle: BillingCycle,
): string => {
  const price = getPlanCyclePrice(plan, cycle);
  const suffix = cycle === 'yearly' ? 'year' : 'month';
  const currency = getPlanCurrency(plan);
  if (typeof price !== 'number') return `Custom ${suffix} pricing`;
  return `${formatPriceAmount(price, currency)} / ${suffix}`;
};
